import { useEffect, useCallback, useRef } from 'react';
import {
    addMessageListener,
    addReconnectListener,
    getSharedSocket,
    send,
} from '../lib/ws/SocketHub';

export function useSharedWS(
    path: string,
    onMessage?: (ev: MessageEvent) => void,
    onReconnect?: () => void,
    options?: { enabled?: boolean }
) {
    const enabled = options?.enabled ?? true;

    const onMessageRef = useRef(onMessage);
    const onReconnectRef = useRef(onReconnect);

    useEffect(() => {
        onMessageRef.current = onMessage;
        onReconnectRef.current = onReconnect;
    });

    const stableOnMessage = useCallback((ev: MessageEvent) => {
        if (onMessageRef.current) onMessageRef.current(ev);
    }, []);

    const stableOnReconnect = useCallback(() => {
        if (onReconnectRef.current) onReconnectRef.current();
    }, []);

    const noopSend = useCallback(() => {}, []);

    useEffect(() => {
        if (!enabled) return;

        const offMessage = onMessageRef.current
            ? addMessageListener(path, stableOnMessage)
            : () => {};
        const offReconnect = onReconnectRef.current
            ? addReconnectListener(path, stableOnReconnect)
            : () => {};
        getSharedSocket(path);

        return () => {
            offMessage();
            offReconnect();
        };
    }, [path, stableOnMessage, stableOnReconnect, enabled]);

    return {
        send: enabled ? (payload: unknown) => send(path, payload) : noopSend,
        socket: enabled ? getSharedSocket(path) : undefined,
    };
}
